import { useCallback, useRef, useState } from "react";
import {
    ActionButton,
    Button,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    TextArea,
} from "@jhool-io/fe-components";
import { useIsMutating } from "@tanstack/react-query";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";

import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import {
    IEditStandardTestingNotePayload,
    IGetClientNotes,
    ISessionNote,
} from "../../../../modules/notes/types/notes.types";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import { STANDARD_TESTING_NOTE_LABELS } from "../../../../utils/constants";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";

import DeleteIcon from "../../../../icons/Delete";
import { IViewFile } from "../../../../utils/types";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

interface EditStandardNoteTemplateTypeProps {
    noteDetails: ISessionNote;
}

export default function EditStandardNoteTemplateType({
    noteDetails,
}: EditStandardNoteTemplateTypeProps) {
    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);

    const isSigning = useRef(false);

    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);

    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };
    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        register,
        handleSubmit,
        getValues,
        control,
        setValue,
        watch,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditStandardTestingNotePayload, { isDraft: boolean }>({
        resolver: yupResolver(
            yup.object({
                duration: yup.string(),
                diagnosis_codes: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .min(1, "Diagnosis is required")
                        .required("Diagnosis is required"),
                    otherwise: yup
                        .array()
                        .max(1, "You can only select 1 diagnosis")
                        .nullable(),
                }),
                secondary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                tertiary_diagnosis: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_one: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                additional_diagnosis_two: yup
                    .array()
                    .nullable()
                    .max(1, "You can only select 1 diagnosis"),
                summary: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("Field is required")
                        .min(20, "Summary must contain at least 20 characters"),
                    otherwise: yup.string(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue("secondary_diagnosis", [
            note.note_content.secondary_diagnosis,
        ]);
        setValue("tertiary_diagnosis", [note.note_content.tertiary_diagnosis]);
        setValue("additional_diagnosis_one", [
            note.note_content.additional_diagnosis_one,
        ]);
        setValue("additional_diagnosis_two", [
            note.note_content.additional_diagnosis_two,
        ]);
        setValue("summary", note.note_content.summary);
        setValue("duration", note.note_content.duration);
        setShowPrompt(false);
    };

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            <div>
                {notes && notes.data.length > 0 && (
                    <div className="flex justify-end mb-[--spacing-header]">
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <FilterButton>
                                    Load previous session note
                                </FilterButton>
                            </DropdownTrigger>
                            <DropdownContent width="auto" align="end">
                                {notes &&
                                    notes?.data?.map((note) => (
                                        <DropdownItem
                                            key={note.note_id}
                                            onClick={() => {
                                                setShowPrompt(true);
                                                setSelectedNote(note);
                                            }}
                                        >
                                            <div>
                                                {makeStringFirstLetterCapital(
                                                    removeEnumUnderscore(
                                                        note.note_type
                                                    )
                                                )}{" "}
                                                - Note content Details -{" "}
                                                {formatDate(
                                                    note.date_of_service
                                                )}
                                            </div>
                                        </DropdownItem>
                                    ))}
                            </DropdownContent>
                        </Dropdown>
                    </div>
                )}
                <form
                    onSubmit={handleSubmit(handleSignNote)}
                    aria-label="edit-form"
                    id="edit-note-form"
                >
                    <div className="mb-[--spacing-header]">
                        <NoteFormInfoBox />
                    </div>
                    <div>
                        <div className="fg">
                            <Controller
                                name="diagnosis_codes"
                                control={control}
                                render={({ field }) => (
                                    <DiagnosisCodeSelect
                                        label={
                                            STANDARD_TESTING_NOTE_LABELS.diagnosis_codes
                                        }
                                        value={field.value}
                                        onChange={field.onChange}
                                        isRequired
                                        errorText={
                                            errors.diagnosis_codes?.type ===
                                            "typeError"
                                                ? "Diagnosis codes are required"
                                                : errors.diagnosis_codes
                                                      ?.message
                                        }
                                        isMulti
                                    />
                                )}
                            />
                        </div>
                        <div className="fg">
                            <Controller
                                name="secondary_diagnosis"
                                control={control}
                                render={({ field }) => (
                                    <DiagnosisCodeSelect
                                        label={
                                            STANDARD_TESTING_NOTE_LABELS.secondary_diagnosis
                                        }
                                        value={field.value}
                                        onChange={field.onChange}
                                        errorText={
                                            errors.secondary_diagnosis?.message
                                        }
                                        isOptional
                                        isMulti
                                    />
                                )}
                            />
                        </div>
                        <div className="fg">
                            <Controller
                                name="tertiary_diagnosis"
                                control={control}
                                render={({ field }) => (
                                    <DiagnosisCodeSelect
                                        label={
                                            STANDARD_TESTING_NOTE_LABELS.tertiary_diagnosis
                                        }
                                        value={field.value}
                                        onChange={field.onChange}
                                        errorText={
                                            errors.tertiary_diagnosis?.message
                                        }
                                        isOptional
                                        isMulti
                                    />
                                )}
                            />
                        </div>
                        <div className="fg">
                            <Controller
                                name="additional_diagnosis_one"
                                control={control}
                                render={({ field }) => (
                                    <DiagnosisCodeSelect
                                        label={
                                            STANDARD_TESTING_NOTE_LABELS.additional_diagnosis_one
                                        }
                                        value={field.value}
                                        onChange={field.onChange}
                                        errorText={
                                            errors.additional_diagnosis_one
                                                ?.message
                                        }
                                        isOptional
                                        isMulti
                                    />
                                )}
                            />
                        </div>
                        <div className="fg">
                            <Controller
                                name="additional_diagnosis_two"
                                control={control}
                                render={({ field }) => (
                                    <DiagnosisCodeSelect
                                        label={
                                            STANDARD_TESTING_NOTE_LABELS.additional_diagnosis_two
                                        }
                                        value={field.value}
                                        onChange={field.onChange}
                                        errorText={
                                            errors.additional_diagnosis_two
                                                ?.message
                                        }
                                        isOptional
                                        isMulti
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <h3 className="note-label-opt">
                        {STANDARD_TESTING_NOTE_LABELS.files}
                    </h3>
                    <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                        <FilePicker.FilePicker
                            onDrop={onDrop}
                            disabled={isAddDocMutating > 0}
                        >
                            <FilePicker.FilePickerIcon />
                            <FilePicker.FilePickerMeta description="Click to upload note files" />
                        </FilePicker.FilePicker>
                        {selectedFiles && selectedFiles.length > 0 ? (
                            <div className="flex flex-col gap-y-[--spacing-items]">
                                <FilePicker.FilePickerSelectedFiles
                                    selectedFiles={selectedFiles}
                                    handleRemoveSelectedFile={
                                        handleRemoveSelectedFile
                                    }
                                    wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                                />
                                <Button
                                    className="self-start"
                                    onClick={() =>
                                        handleAddNoteDocument(
                                            selectedFiles,
                                            () => setSelectedFiles(null)
                                        )
                                    }
                                    disabled={isAddDocMutating > 0}
                                >
                                    Save files
                                </Button>
                                {uploadProgress > 0 && uploadProgress < 100 && (
                                    <Progress.Root value={uploadProgress}>
                                        <Progress.Label>
                                            Uploading {selectedFiles.length}{" "}
                                            {selectedFiles.length === 1
                                                ? "file"
                                                : "files"}
                                            ...
                                        </Progress.Label>
                                        <Progress.Value />
                                        <Progress.Track>
                                            <Progress.Indicator />
                                        </Progress.Track>
                                    </Progress.Root>
                                )}
                            </div>
                        ) : null}
                        {noteDetails?.note_content_documents &&
                        noteDetails?.note_content_documents.length > 0 ? (
                            <div className="flex flex-col gap-y-[var(--spacing-items)]">
                                {noteDetails.note_content_documents.map(
                                    (doc) => (
                                        <div
                                            key={doc.note_content_document_id}
                                            className="flex items-center gap-x-3"
                                        >
                                            <ActionButton
                                                variant="can-hover"
                                                size="auto"
                                                className="h-7 !text-text text-caption underline"
                                                actionTitle="View file"
                                                onClick={() => {
                                                    setDocInView(doc);
                                                }}
                                            >
                                                <span>{`${displayNameInRightFormat(
                                                    {
                                                        firstName:
                                                            doc.uploaded_by
                                                                .first_name,
                                                        lastName:
                                                            doc.uploaded_by
                                                                .last_name,
                                                    },
                                                    false
                                                )} -  ${
                                                    doc.document_name
                                                }`}</span>
                                            </ActionButton>
                                            <ActionButton
                                                variant="can-hover"
                                                aria-label="delete icon"
                                                size="auto"
                                                className="h-7"
                                                onClick={() =>
                                                    handleDeleteNoteContentDocument(
                                                        doc.note_content_document_id
                                                    )
                                                }
                                                disabled={
                                                    isDeleteDocMutating > 0
                                                }
                                                actionTitle="Delete file from note content"
                                            >
                                                <DeleteIcon />
                                            </ActionButton>
                                        </div>
                                    )
                                )}
                            </div>
                        ) : null}
                    </div>

                    <h3 className="note-label-req !text-base">
                        {STANDARD_TESTING_NOTE_LABELS.summary}
                    </h3>
                    <p className="note-label">
                        May include: Plan and specific purpose for testing,
                        listing tests scored. Refer to final report included in
                        chart for further information on historical data
                    </p>
                    <div className="fg">
                        <TextArea
                            {...register("summary")}
                            defaultValue={noteDetails.note_content?.summary}
                            hasError={!!errors.summary}
                            errorText={errors.summary?.message}
                            isRequired
                        />
                    </div>

                    <div className="fg-info fg-line">
                        <p>Sign note here</p>
                        <div className="fg">
                            <Input
                                {...register("signature")}
                                hasError={!!errors.signature}
                                errorText={errors.signature?.message}
                                label="Provider's Initials"
                                autoComplete="off"
                                isRequired
                            />
                        </div>
                    </div>
                    <div>
                        <EditNoteFooter
                            onSaveButtonClick={() => {
                                isSigning.current = false;
                                handleSubmit(handleEditDraftNote)();
                            }}
                            noteDOS={noteDetails.date_of_service}
                            onSubmitButtonClick={() => {
                                isSigning.current = true;
                            }}
                        />
                    </div>
                </form>
            </div>
        </>
    );
}
